/**
 * Global Shortcuts Hook
 * 
 * Custom hook for managing global keyboard shortcuts:
 * - Ctrl+K / Cmd+K: Open global search
 * - Ctrl+/ / Cmd+/: Open shortcuts help
 * - Ctrl+N: New item (contextual)
 * - Ctrl+E: Edit item (contextual)
 * - Ctrl+D: Delete item (contextual)
 * - Escape: Close modals
 * - Input field conflict management
 * - Multi-platform support (Cmd on Mac, Ctrl on others)
 * - Custom shortcut callbacks
 * 
 * @module hooks/useGlobalShortcuts
 * @param {Object} [callbacks={}] - Shortcut callback functions
 * @returns {Object} - Shortcut information and helpers
 */
import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

const useGlobalShortcuts = (callbacks = {}) => {
  const { _t } = useTranslation();
  const {
    onOpenSearch,
    onCloseModal,
    onToggleTheme,
    onOpenNotifications,
    onOpenHelp,
    onNew,
    onEdit,
    onDelete,
    onSave,
    onRefresh
  } = callbacks;

  // Platform detection
  const isMac = typeof window !== 'undefined' &&
    /Mac|iPod|iPhone|iPad/.test(window.navigator.platform);

  // Check if active element is an input field
  const isInputActive = useCallback(() => {
    const activeElement = document.activeElement;
    const inputTypes = ['input', 'textarea', 'select'];
    const isContentEditable = activeElement?.contentEditable === 'true';

    return inputTypes.includes(activeElement?.tagName?.toLowerCase()) ||
      isContentEditable ||
      activeElement?.getAttribute('role') === 'textbox';
  }, []);

  // Main shortcut handler
  const handleKeyDown = useCallback((event) => {
    const { key, ctrlKey, metaKey, _altKey, shiftKey } = event;

    // Use Cmd on Mac, Ctrl on other platforms
    const isModifierPressed = isMac ? metaKey : ctrlKey;

    // Ctrl+K / Cmd+K shortcut for search
    if (isModifierPressed && key.toLowerCase() === 'k' && onOpenSearch) {
      event.preventDefault();
      event.stopPropagation();
      if (!isInputActive()) {
        onOpenSearch();
      }
      return;
    }

    // Ctrl+/ / Cmd+/ shortcut for shortcuts help
    if (isModifierPressed && key === '/' && onOpenHelp) {
      event.preventDefault();
      event.stopPropagation();
      onOpenHelp();
      return;
    }

    // ? shortcut for help (alternative)
    if (key === '?' && !isInputActive() && onOpenHelp) {
      event.preventDefault();
      onOpenHelp();
      return;
    }

    // Escape shortcut to close modals
    if (key === 'Escape' && onCloseModal) {
      onCloseModal();
      return;
    }

    // Ctrl+N / Cmd+N shortcut for new (if not in input)
    if (isModifierPressed && key.toLowerCase() === 'n' && !shiftKey && onNew) {
      event.preventDefault();
      if (!isInputActive()) {
        onNew();
      }
      return;
    }

    // Ctrl+E / Cmd+E shortcut for edit
    if (isModifierPressed && key.toLowerCase() === 'e' && onEdit) {
      event.preventDefault();
      if (!isInputActive()) {
        onEdit();
      }
      return;
    }

    // Ctrl+D / Cmd+D shortcut to delete (with confirmation)
    if (isModifierPressed && key.toLowerCase() === 'd' && onDelete) {
      event.preventDefault();
      if (!isInputActive()) {
        onDelete();
      }
      return;
    }

    // Ctrl+S / Cmd+S shortcut for save
    if (isModifierPressed && key.toLowerCase() === 's' && onSave) {
      event.preventDefault();
      onSave();
      return;
    }

    // Ctrl+R / Cmd+R shortcut for refresh (override browser refresh)
    if (isModifierPressed && key.toLowerCase() === 'r' && !shiftKey && onRefresh) {
      event.preventDefault();
      onRefresh();
      return;
    }

    // Ctrl+Shift+T / Cmd+Shift+T shortcut to toggle theme
    if (isModifierPressed && shiftKey && key.toLowerCase() === 't' && onToggleTheme) {
      event.preventDefault();
      onToggleTheme();
      return;
    }

    // Ctrl+Shift+N / Cmd+Shift+N shortcut for notifications
    if (isModifierPressed && shiftKey && key.toLowerCase() === 'n' && onOpenNotifications) {
      event.preventDefault();
      onOpenNotifications();
      return;
    }

  }, [isMac, isInputActive, onOpenSearch, onCloseModal, onToggleTheme, onOpenNotifications, onOpenHelp, onNew, onEdit, onDelete, onSave, onRefresh]);

  // Setup and cleanup event listeners
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Return useful information
  const modKey = isMac ? '⌘' : 'Ctrl';

  return {
    isMac,
    modKey,
    shortcuts: {
      search: `${modKey}+K`,
      help: `${modKey}+/`,
      new: `${modKey}+N`,
      edit: `${modKey}+E`,
      delete: `${modKey}+D`,
      save: `${modKey}+S`,
      refresh: `${modKey}+R`,
      theme: `${modKey}+Shift+T`,
      notifications: `${modKey}+Shift+N`,
      close: 'Escape'
    },
    // Helper to format shortcut for display
    formatShortcut: (action) => {
      const shortcuts = {
        search: { key: 'K', mod: true },
        help: { key: '/', mod: true },
        new: { key: 'N', mod: true },
        edit: { key: 'E', mod: true },
        delete: { key: 'D', mod: true },
        save: { key: 'S', mod: true },
        refresh: { key: 'R', mod: true }
      };
      const s = shortcuts[action];
      if (!s) return '';
      return s.mod ? `${modKey}+${s.key}` : s.key;
    }
  };
};

export default useGlobalShortcuts;


